
import React, { useState, useRef } from 'react';
import { SchoolSettings, Announcement, UserRole } from '../types';
import { Settings as SettingsIcon, Save, Building2, User, Fingerprint, MapPin, Image as ImageIcon, Trash2, Plus, AlertTriangle, Info, CheckCircle, Upload, X, GraduationCap, RefreshCw, Sliders, Calendar } from 'lucide-react';

interface Props {
  settings: SchoolSettings;
  onUpdate: (settings: SchoolSettings) => Promise<any>;
  role?: UserRole;
}

const SettingsView: React.FC<Props> = ({ settings, onUpdate, role = UserRole.Admin }) => {
  const [formData, setFormData] = useState<SchoolSettings>(settings);
  
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const deptLogoInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = role === UserRole.Admin;

  const days = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];

  React.useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>, isDept = false) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 500 * 1024) { // 500KB limit to fit in Firestore 1MB doc
        setError("Ukuran logo terlalu besar (maksimal 500KB). Silakan kompres gambar Anda.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isDept) {
          setFormData({ ...formData, departmentLogoUrl: reader.result as string });
        } else {
          setFormData({ ...formData, logoUrl: reader.result as string });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddAnnouncement = () => {
    const newAnn: Announcement = {
      id: Date.now().toString(),
      text: '',
      type: 'info'
    };
    setFormData({
      ...formData,
      announcements: [...(formData.announcements || []), newAnn]
    });
  };

  const handleRemoveAnnouncement = (id: string) => {
    setFormData({
      ...formData,
      announcements: (formData.announcements || []).filter(a => a.id !== id)
    });
  };

  const handleUpdateAnnouncement = (id: string, updates: Partial<Announcement>) => {
    setFormData({
      ...formData,
      announcements: (formData.announcements || []).map(a => a.id === id ? { ...a, ...updates } : a)
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      const result = await onUpdate(formData);
      if (result && (result as any).error) {
        setError((result as any).message || "Gagal menyimpan ke database.");
      } else {
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 3000);
      }
    } catch (err) {
      console.error("Failed to save settings:", err);
      setError("Terjadi kesalahan jaringan.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-20">
      <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
          <div>
            <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-3">Pengaturan Sekolah</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Konfigurasi identitas sekolah untuk laporan dan administrasi.</p>
          </div>
          <SettingsIcon className="text-indigo-600 hidden md:block" size={48} />
        </div>

        <form onSubmit={handleSave} className="max-w-4xl space-y-10">
          {/* Konfigurasi Sistem - Student Access controls (Visible to Admin & Guru) */}
          <div className="space-y-8">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-500 text-white rounded-2xl shadow-lg shadow-amber-500/20">
                <SettingsIcon size={20} />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Konfigurasi Akses Siswa</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">KONTROL AKSES DAN FITUR HALAMAN SISWA</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div 
                onClick={() => setFormData({ ...formData, isStudentMaintenanceMode: !formData.isStudentMaintenanceMode })}
                className={`p-6 rounded-[2rem] border-2 cursor-pointer transition-all ${
                  formData.isStudentMaintenanceMode 
                    ? 'bg-amber-50 border-amber-500 dark:bg-amber-500/10' 
                    : 'bg-slate-50 border-transparent dark:bg-slate-800/40 hover:border-slate-200 dark:hover:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl ${formData.isStudentMaintenanceMode ? 'bg-amber-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                    <AlertTriangle size={20} />
                  </div>
                  <div className={`w-12 h-6 rounded-full relative transition-colors ${formData.isStudentMaintenanceMode ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.isStudentMaintenanceMode ? 'right-1' : 'left-1'}`}></div>
                  </div>
                </div>
                <h4 className="font-black text-slate-900 dark:text-white mb-1">Mode Maintenance Siswa</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Jika diaktifkan, halaman siswa akan dikunci dan menampilkan pesan maintenance.</p>
              </div>

              <div 
                onClick={() => setFormData({ ...formData, showGradesToStudents: !formData.showGradesToStudents })}
                className={`p-6 rounded-[2rem] border-2 cursor-pointer transition-all ${
                  formData.showGradesToStudents 
                    ? 'bg-indigo-50 border-indigo-500 dark:bg-indigo-500/10' 
                    : 'bg-slate-50 border-transparent dark:bg-slate-800/40 hover:border-slate-200 dark:hover:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl ${formData.showGradesToStudents ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                    <GraduationCap size={20} />
                  </div>
                  <div className={`w-12 h-6 rounded-full relative transition-colors ${formData.showGradesToStudents ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.showGradesToStudents ? 'right-1' : 'left-1'}`}></div>
                  </div>
                </div>
                <h4 className="font-black text-slate-900 dark:text-white mb-1">Tampilkan Nilai ke Siswa</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Kontrol apakah siswa dapat melihat rekap nilai mereka di profil siswa.</p>
              </div>
            </div>
          </div>

          {isAdmin && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pt-10 border-t border-slate-100 dark:border-slate-800">
                {/* Identitas Sekolah */}
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-2">
                <Building2 size={18} className="text-indigo-600" />
                <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">Identitas Sekolah</h3>
              </div>
              
              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Pemerintah Daerah</label>
                <input 
                  type="text" 
                  value={formData.governmentName || ''}
                  onChange={e => setFormData({...formData, governmentName: e.target.value})}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="Contoh: PEMERINTAH KABUPATEN CIAMIS"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Dinas Pendidikan</label>
                <input 
                  type="text" 
                  value={formData.departmentName || ''}
                  onChange={e => setFormData({...formData, departmentName: e.target.value})}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="Contoh: DINAS PENDIDIKAN"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Nama Sekolah</label>
                <input 
                  type="text" 
                  value={formData.schoolName}
                  onChange={e => setFormData({...formData, schoolName: e.target.value})}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="Contoh: SMP NEGERI 1 KAWALI"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Alamat Lengkap</label>
                <textarea 
                  value={formData.address}
                  onChange={e => setFormData({...formData, address: e.target.value})}
                  rows={3}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="Jl. Raya Kawali No. 1..."
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Baris Tambahan Kop (Opsional)</label>
                <input 
                  type="text" 
                  value={formData.additionalKopLine || ''}
                  onChange={e => setFormData({...formData, additionalKopLine: e.target.value})}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="Contoh: Telp: (0265) 123456, Email: sekolah@sch.id"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Nama Kabupaten</label>
                <input 
                  type="text" 
                  value={formData.kabupaten || ''}
                  onChange={e => setFormData({...formData, kabupaten: e.target.value})}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="Contoh: KABUPATEN CIAMIS"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Kecamatan</label>
                <input 
                  type="text" 
                  value={formData.kecamatan || ''}
                  onChange={e => setFormData({...formData, kecamatan: e.target.value})}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="Contoh: Kawali"
                />
              </div>



              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1 flex items-center gap-1.5">
                  <ImageIcon size={12} /> Logo Sekolah
                </label>
                <div className="flex flex-col gap-4">
                  {formData.logoUrl ? (
                    <div className="relative w-32 h-32 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden group">
                      <img src={formData.logoUrl} alt="Logo Preview" className="w-full h-full object-contain p-2" referrerPolicy="no-referrer" />
                      <button 
                        type="button"
                        onClick={() => setFormData({ ...formData, logoUrl: '' })}
                        className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button 
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full aspect-video sm:w-32 sm:h-32 bg-slate-50 dark:bg-slate-800 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-indigo-600 hover:border-indigo-400 transition-all group"
                    >
                      <Upload size={24} className="group-hover:scale-110 transition-transform" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Upload Logo</span>
                    </button>
                  )}
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <div className="flex-1">
                    <label className="text-[9px] font-black text-slate-400 uppercase block mb-1.5 ml-1">Atau masukkan URL Logo</label>
                    <input 
                      type="url" 
                      value={formData.logoUrl}
                      onChange={e => setFormData({...formData, logoUrl: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:text-white text-xs font-bold transition-all"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1 flex items-center gap-1.5">
                  <ImageIcon size={12} /> Logo Dinas
                </label>
                <div className="flex flex-col gap-4">
                  {formData.departmentLogoUrl ? (
                    <div className="relative w-32 h-32 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden group">
                      <img src={formData.departmentLogoUrl} alt="Logo Dinas Preview" className="w-full h-full object-contain p-2" referrerPolicy="no-referrer" />
                      <button 
                        type="button"
                        onClick={() => setFormData({ ...formData, departmentLogoUrl: '' })}
                        className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button 
                      type="button"
                      onClick={() => deptLogoInputRef.current?.click()}
                      className="w-full aspect-video sm:w-32 sm:h-32 bg-slate-50 dark:bg-slate-800 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-indigo-600 hover:border-indigo-400 transition-all group"
                    >
                      <Upload size={24} className="group-hover:scale-110 transition-transform" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Upload Logo Dinas</span>
                    </button>
                  )}
                  <input 
                    type="file" 
                    ref={deptLogoInputRef}
                    onChange={(e) => handleLogoUpload(e, true)}
                    accept="image/*"
                    className="hidden"
                  />
                  <div className="flex-1">
                    <label className="text-[9px] font-black text-slate-400 uppercase block mb-1.5 ml-1">Atau masukkan URL Logo Dinas</label>
                    <input 
                      type="url" 
                      value={formData.departmentLogoUrl}
                      onChange={e => setFormData({...formData, departmentLogoUrl: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:text-white text-xs font-bold transition-all"
                      placeholder="https://example.com/logo-dinas.png"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pejabat Berwenang */}
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-2">
                <User size={18} className="text-indigo-600" />
                <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">Pejabat Berwenang</h3>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Nama Kepala Sekolah</label>
                <input 
                  type="text" 
                  value={formData.principalName}
                  onChange={e => setFormData({...formData, principalName: e.target.value})}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="Nama Lengkap & Gelar"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1 flex items-center gap-1.5">
                  <Fingerprint size={12} /> NIP Kepala Sekolah
                </label>
                <input 
                  type="text" 
                  value={formData.principalNip}
                  onChange={e => setFormData({...formData, principalNip: e.target.value})}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="19XXXXXXXXXXXXXXX"
                />
              </div>

              <div className="p-6 bg-indigo-50 dark:bg-indigo-500/5 rounded-[2rem] border border-indigo-100 dark:border-indigo-500/20">
                <p className="text-xs text-indigo-700 dark:text-indigo-300 font-bold leading-relaxed italic">
                  * Data ini akan digunakan secara otomatis sebagai Kop Surat dan Tanda Tangan pada setiap laporan yang diekspor dari sistem ini.
                </p>
              </div>
            </div>
          </div>

          {/* Pengumuman Sekolah */}
          <div className="pt-10 border-t border-slate-100 dark:border-slate-800 space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg shadow-indigo-500/20">
                  <Info size={20} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Pengumuman Sekolah</h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">KELOLA INFORMASI DI DASHBOARD GURU</p>
                </div>
              </div>
              <button 
                type="button"
                onClick={handleAddAnnouncement}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-indigo-600 hover:text-white transition-all active:scale-95"
              >
                <Plus size={14} /> Tambah Pengumuman
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {(formData.announcements || []).length > 0 ? (
                (formData.announcements || []).map((ann, idx) => (
                  <div key={ann.id} className="group p-6 bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700 rounded-3xl flex flex-col md:flex-row gap-6 items-start md:items-center transition-all hover:border-indigo-200 dark:hover:border-indigo-900/50">
                    <div className="flex-1 w-full space-y-4">
                      <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full sm:w-48">
                          <label className="text-[9px] font-black text-slate-400 uppercase block mb-1.5 ml-1">Tipe</label>
                          <select 
                            value={ann.type}
                            onChange={e => handleUpdateAnnouncement(ann.id, { type: e.target.value as any })}
                            className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500/20 dark:text-white"
                          >
                            <option value="info">Informasi (Biru)</option>
                            <option value="urgent">Penting (Merah)</option>
                            <option value="success">Sukses (Hijau)</option>
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="text-[9px] font-black text-slate-400 uppercase block mb-1.5 ml-1">Isi Pengumuman</label>
                          <input 
                            type="text" 
                            value={ann.text}
                            onChange={e => handleUpdateAnnouncement(ann.id, { text: e.target.value })}
                            placeholder="Tulis pengumuman di sini..."
                            className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500/20 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={() => handleRemoveAnnouncement(ann.id)}
                      className="p-3 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-xl transition-all"
                      title="Hapus Pengumuman"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))
              ) : (
                <div className="py-10 text-center border-2 border-dashed border-slate-100 dark:border-slate-800 rounded-3xl">
                  <p className="text-xs text-slate-400 font-bold italic">Belum ada pengumuman. Klik "Tambah Pengumuman" untuk membuat baru.</p>
                </div>
              )}
            </div>
          </div>
        </>)}

          <div className="pt-10 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-center gap-4">
            <button 
              type="submit"
              disabled={isSaving}
              className={`flex items-center justify-center gap-3 px-10 py-5 bg-indigo-600 text-white font-black rounded-3xl hover:bg-indigo-700 shadow-2xl shadow-indigo-500/30 active:scale-95 transition-all uppercase tracking-widest text-sm w-full sm:w-auto ${isSaving ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isSaving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Save size={20} />
              )}
              {isSaving ? 'Menyimpan...' : 'Simpan Pengaturan'}
            </button>
            
            {isSaved && (
              <div className="flex items-center gap-2 text-green-600 font-black text-sm animate-fade-in">
                <div className="w-2 h-2 bg-green-600 rounded-full animate-ping"></div>
                PENGATURAN BERHASIL DIPERBARUI!
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-rose-600 font-black text-sm animate-fade-in">
                <AlertTriangle size={18} />
                {error}
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Preview Kop (Admin Only) */}
      {isAdmin && (
        <div className="bg-slate-50 dark:bg-slate-800/20 p-10 rounded-[2.5rem] border border-dashed border-slate-200 dark:border-slate-800">
        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 text-center">Preview Kop Laporan</h3>
        <div className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 max-w-2xl mx-auto flex items-center gap-6">
          {formData.departmentLogoUrl && (
            <img src={formData.departmentLogoUrl} alt="Logo Dinas" className="w-20 h-20 object-contain shrink-0" referrerPolicy="no-referrer" />
          )}
          {formData.logoUrl && (
            <img src={formData.logoUrl} alt="Logo Sekolah" className="w-20 h-20 object-contain shrink-0" referrerPolicy="no-referrer" />
          )}
          <div className="flex-1 text-center space-y-1">
            <p className="font-black text-slate-900 dark:text-white uppercase text-lg leading-none">{formData.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
            <p className="font-black text-slate-900 dark:text-white uppercase text-lg leading-none">{formData.departmentName || 'DINAS PENDIDIKAN'}</p>
            <p className="font-black text-indigo-600 uppercase text-2xl leading-tight">{formData.schoolName || 'NAMA SEKOLAH'}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium italic">{formData.address || 'Alamat Sekolah...'}</p>
            {formData.additionalKopLine && (
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{formData.additionalKopLine}</p>
            )}
            <div className="h-1 bg-slate-900 dark:bg-white w-full mt-4"></div>
            <div className="h-0.5 bg-slate-900 dark:bg-white w-full mt-0.5"></div>
          </div>
        </div>
      </div>)}
    </div>
  );
};

export default SettingsView;
